import React, { useState, useEffect } from "react";
import { IoCloseCircleOutline } from "react-icons/io5";
import {
  HouseSimple,
  Eye,
  IdentificationCard,
  User,
  CloudArrowUp,
} from "@phosphor-icons/react";
import { IoCloudUploadOutline } from "react-icons/io5"; // Updated icon
import axios from "axios";

const Profile = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [profileData, setProfileData] = useState(null);
  const [updatedData, setUpdatedData] = useState({});
  const userAutoId = sessionStorage.getItem("accessToken");
  const [themeColor, setThemeColor] = useState("#fffff");
  const [showGstInput, setShowGstInput] = useState(false);
  const [webFont, setWebFont] = useState("");

  const [profilePhotoFile, setProfilePhotoFile] = useState(null);

  useEffect(() => {
    const storedStyle = sessionStorage.getItem("UIStyle");
    if (storedStyle) {
      const parsedStyle = JSON.parse(storedStyle);
      setWebFont(parsedStyle.web_font);
      setThemeColor(parsedStyle.web_theme_colour);
    }
  }, []);

  function lightenHexColor(hex, percent) {
    const num = parseInt(hex.replace("#", ""), 16);
    let r = (num >> 16) + 255 * percent;
    let g = ((num >> 8) & 0x00ff) + 255 * percent;
    let b = (num & 0x0000ff) + 255 * percent;

    r = Math.max(0, Math.min(255, Math.round(r)));
    g = Math.max(0, Math.min(255, Math.round(g)));
    b = Math.max(0, Math.min(255, Math.round(b)));

    return `rgb(${r}, ${g}, ${b})`;
  }

  function darkenHexColor(hex, percent) {
    const num = parseInt(hex.replace("#", ""), 16);
    let r = (num >> 16) - 255 * percent;
    let g = ((num >> 8) & 0x00ff) - 255 * percent;
    let b = (num & 0x0000ff) - 255 * percent;

    r = Math.max(0, Math.min(255, Math.round(r)));
    g = Math.max(0, Math.min(255, Math.round(g)));
    b = Math.max(0, Math.min(255, Math.round(b)));

    return `rgb(${r}, ${g}, ${b})`;
  }

  useEffect(() => {
    if (!userAutoId) return;

    const fetchProfile = async () => {
      try {
        const response = await axios.post(
          `${process.env.REACT_APP_API_BASE_URL}/get_user_profile`,
          { user_auto_id: userAutoId },
          { headers: { "Content-Type": "application/json" } }
        );

        console.log("API Response:", response.data);

        if (response.data.status === "1" && response.data.data) {
          setProfileData(response.data.data);
          setUpdatedData(response.data.data);
        } else {
          console.error("Invalid response format:", response.data);
          setProfileData(null);
        }
      } catch (error) {
        console.error("API Error:", error);
        setProfileData(null);
      }
    };

    fetchProfile();
  }, [userAutoId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setUpdatedData({ ...updatedData, [name]: value });
  };

  const handleProfilePhotoChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setProfilePhotoFile(file);
    }
  };

  const handleUpdateProfile = async () => {
    try {
      if (profilePhotoFile) {
        const formData = new FormData();
        formData.append("user_auto_id", userAutoId);
        formData.append("name", updatedData.name || "");
        formData.append("email_id", updatedData.email_id || "");
        formData.append("mobile_number", updatedData.mobile_number || "");
        formData.append("address", updatedData.address || "");
        formData.append("city", updatedData.city || "");

        if (updatedData.gst_number) {
          formData.append("gst_number", updatedData.gst_number);
        }

        formData.append("profile_photo", profilePhotoFile);

        const response = await axios.post(
          `${process.env.REACT_APP_API_BASE_URL}/update_user_profile`,
          formData,
          {
            headers: { "Content-Type": "multipart/form-data" },
          }
        );

        const result = response.data;

        if (result.status === "1" && result.data) {
          setProfileData(result.data);
          setIsModalOpen(false);
          setProfilePhotoFile(null);
        } else {
          console.error("Update failed:", result);
        }

        return;
      }

      const payload = {
        user_auto_id: userAutoId,
        name: updatedData.name,
        email_id: updatedData.email_id,
        mobile_number: updatedData.mobile_number,
        address: updatedData.address,
        city: updatedData.city,
      };

      if (updatedData.gst_number) {
        payload.gst_number = updatedData.gst_number;
      }

      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/update_user_profile`,
        payload,
        {
          headers: { "Content-Type": "application/json" },
        }
      );

      const result = response.data;

      if (result.status === "1" && result.data) {
        setProfileData(result.data);
        setIsModalOpen(false);
      } else {
        console.error("Update failed:", result);
      }
    } catch (error) {
      console.error("API Update Error:", error);
    }
  };

  return (
    <>
      <div className={`p-6 ${webFont}`}>
        <div className="flex items-center justify-between px-6 py-2 mb-6 bg-white border border-gray-200 rounded-lg shadow-sm">
          <h2 className="text-xl font-bold">
            Welcome back, {profileData?.name || "User"}!
          </h2>
        </div>

        {profileData && (
          <div className="flex flex-col items-center p-6 mt-6 bg-white rounded-md shadow-sm md:flex-row md:items-start">
            <div className="relative flex items-center justify-center w-48 h-48 mb-6 overflow-hidden bg-gray-100 rounded-xl">
              {profileData?.profile_photo ? (
                <img
                  src={`https://grobizapp-n.s3.ap-south-1.amazonaws.com/profile/${profileData.profile_photo}`}
                  alt="Profile"
                  className="object-cover w-full h-full"
                />
              ) : (
                <User
                  className=" text-[14rem]"
                  weight="thin"
                  style={{ color: themeColor }}
                />
              )}
            </div>

            <div className="grid grid-cols-2 md:ml-12 gap-x-24">
              <div>
                {profileData?.name && (
                  <>
                    <p className="text-sm font-bold text-black">Name</p>
                    <p className="text-sm text-gray-600">{profileData.name}</p>
                  </>
                )}
                {profileData?.mobile_number && (
                  <>
                    <p className="mt-4 text-sm font-bold text-black">Email</p>
                    <p className="text-sm text-gray-600">
                      {profileData.email_id}
                    </p>
                  </>
                )}

                {profileData?.mobile_number && (
                  <>
                    <p className="mt-4 font-bold text-black ttext-sm ">
                      Mobile
                    </p>
                    <p className="text-sm text-gray-600">
                      {profileData.mobile_number}
                    </p>
                  </>
                )}
              </div>
              <div>
                {profileData?.address && (
                  <>
                    <p className="text-sm font-bold text-black">Address</p>
                    <p className="text-sm text-gray-600">
                      {profileData.address}
                    </p>
                  </>
                )}
                {profileData?.city && (
                  <>
                    <p className="mt-4 text-sm font-bold text-black">City</p>
                    <p className="text-sm text-gray-600 ">
                      {profileData.city}
                    </p>
                  </>
                )}

                {profileData?.gst_number && (
                  <>
                    <p className="mt-4 text-sm font-bold text-black">
                      GST Number
                    </p>
                    <p className="text-sm text-gray-600">
                      {profileData.gst_number}
                    </p>
                  </>
                )}
              </div>
            </div>

            <button
              onClick={() => setIsModalOpen(true)}
              className="px-4 py-2 mt-4 ml-auto bg-white border rounded-md md:mt-0 "
              style={{ color: themeColor }}
            >
              Edit Profile
            </button>
          </div>
        )}

        {isModalOpen && (
          <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
            <div className="relative p-6 bg-white rounded-lg shadow-lg w-96">
              <button
                className="absolute text-gray-500 top-2 right-2 hover:text-gray-800"
                onClick={() => {
                  setIsModalOpen(false);
                  setShowGstInput(true);
                }}
              >
                <IoCloseCircleOutline className="text-2xl" />
              </button>

              <h2 className="mb-4 text-xl font-semibold text-center">
                Edit Profile
              </h2>

              <div className="space-y-3">
                <label className="block">
                  <span className="text-gray-600">Profile Photo</span>
                  <input
                    type="file"
                    accept="image/*"
                    className="w-full p-2 border rounded-md"
                    onChange={handleProfilePhotoChange}
                  />
                </label>

                <label className="block">
                  <span className="text-gray-600">Name</span>
                  <input
                    type="text"
                    name="name"
                    className="w-full p-2 border rounded-md"
                    value={updatedData.name || ""}
                    onChange={handleInputChange}
                  />
                </label>

                <label className="block">
                  <span className="text-gray-600">Email</span>
                  <input
                    type="email"
                    name="email_id"
                    className="w-full p-2 border rounded-md"
                    value={updatedData.email_id || ""}
                    onChange={handleInputChange}
                  />
                </label>

                <label className="block">
                  <span className="text-gray-600">Mobile</span>
                  <input
                    type="text"
                    name="mobile_number"
                    className="w-full p-2 border rounded-md"
                    value={updatedData.mobile_number || ""}
                    onChange={handleInputChange}
                  />
                </label>

                <label className="block">
                  <span className="text-gray-600">Address</span>
                  <input
                    type="text"
                    name="address"
                    className="w-full p-2 border rounded-md"
                    value={updatedData.address || ""}
                    onChange={handleInputChange}
                  />
                </label>

                <label className="block">
                  <span className="text-gray-600">City</span>
                  <input
                    type="text"
                    name="city"
                    className="w-full p-2 border rounded-md"
                    value={updatedData.city || ""}
                    onChange={handleInputChange}
                  />
                </label>

                {profileData?.gst_number || showGstInput ? (
                  <label className="block">
                    <span className="text-gray-600">GST Number</span>
                    <input
                      type="text"
                      name="gst_number"
                      className="w-full p-2 border rounded-md"
                      value={updatedData.gst_number || ""}
                      onChange={handleInputChange}
                    />
                  </label>
                ) : (
                  <button
                    type="button"
                    onClick={() => setShowGstInput(true)}
                    className="px-3 py-2 text-sm font-semibold text-blue-600 bg-blue-100 border border-blue-300 rounded-md hover:bg-blue-200"
                  >
                    Add GST Number
                  </button>
                )}
              </div>

              <button
                className="w-full py-2 mt-4 text-white rounded-md"
                onClick={handleUpdateProfile}
                style={{ backgroundColor: themeColor }}
              >
                Update Profile
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default Profile;
